import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { DecimalInput } from '@/components/ui/decimal-input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { toast } from '@/components/ui/use-toast';
import { ArrowLeft, Loader2, CheckCircle2, Clock, AlertTriangle, CalendarClock, Info, Tag, UserRound } from 'lucide-react';
import {
  WEEK_MS, EXTENSION_START, deriveStatus,
  type FollowupStatus as Status, type FollowupArm as Arm,
} from '@/lib/followups-shared';
import { DoseCombobox } from '@/components/assessment/DoseCombobox';
import { scoreSerialConcordance } from '@/lib/followup-concordance';

const armConfig = {
  pregnancy: {
    label: 'Κύηση',
    prefix: 'P',
    cases: 'clinical_cases' as const,
    clinicianResponses: 'clinician_responses' as const,
    followups: 'followup_results' as const,
    labels: 'case_labels' as const,
    caseColumns: 'id,study_case_no,tsh,gestational_week,created_at,status',
  },
  preconception: {
    label: 'Προ της σύλληψης',
    prefix: 'C',
    cases: 'preconception_cases' as const,
    clinicianResponses: 'preconception_clinician_responses' as const,
    followups: 'preconception_followup_results' as const,
    labels: 'preconception_case_labels' as const,
    caseColumns: 'id,study_case_no,tsh,created_at,status',
  },
};

type CaseRow = {
  id: string;
  study_case_no: number | null;
  tsh: number;
  gestational_week?: number;
  created_at: string;
};

type FollowupRow = {
  id: string;
  case_id: string;
  followup_date: string;
  followup_tsh: number;
  dose_prescribed: number | null;
  dose_unchanged: boolean | null;
  on_target: boolean | null;
  over_treated: boolean | null;
  notes: string | null;
};

const weeksBetween = (from: string, to: string) =>
  (new Date(to).getTime() - new Date(from).getTime()) / WEEK_MS;

const addWeeksISO = (from: string, weeks: number) =>
  new Date(new Date(from).getTime() + weeks * WEEK_MS).toISOString().slice(0, 10);

// Protocol §3 per-protocol window: FIRST TSH >=4 and <=8 wk on an unchanged dose.
// That 4 wk floor is strict, but it is an ANALYSIS boundary (in_pp_window /
// is_pp_primary in v_followup_visits) — NOT a save gate. §5 registers the ITT-like
// set as "ANY follow-up TSH ... earliest value used" over a deliberately WIDER
// window, and wider cuts both ways: a 3.5 wk draw is registered ITT-like data just
// as a 12 wk one is. Gating either end in the UI would silently destroy that set.
const PP_MIN_WEEKS = 4;
const PP_MAX_WEEKS = 8;

// Entry sanity floor only. Below this an entry is a mistyped date, not a re-check.
const ENTRY_MIN_WEEKS = 3;

// Trimester-specific target (the pre-registered local aim, OSF secondary hypothesis b):
// pregnancy <2.5 (T1 ≤13 wk) / <3.0 (T2-3); preconception <2.5. Over-treatment = TSH <0.1.
function evalTargets(arm: Arm, tsh: number, indexGA: number | undefined, weeks: number) {
  const over_treated = tsh < 0.1;
  let on_target: boolean;
  if (arm === 'pregnancy') {
    const ga = (indexGA ?? 0) + Math.round(weeks);
    on_target = tsh < (ga <= 13 ? 2.5 : 3.0);
  } else {
    on_target = tsh < 2.5;
  }
  return { on_target, over_treated };
}

const statusRank: Record<Status, number> = { overdue: 0, due: 1, scheduled: 2, done: 3 };

const ArmFollowups = ({ arm }: { arm: Arm }) => {
  const { user } = useAuth();
  const cfg = armConfig[arm];
  const [profileId, setProfileId] = useState<string | null>(null);
  const [cases, setCases] = useState<CaseRow[]>([]);
  // case_id → every post-index TSH for that case, ascending by date (protocol §5
  // needs the whole series: first-in-window is the per-protocol primary, earliest
  // is the ITT-like sensitivity value).
  const [followups, setFollowups] = useState<Record<string, FollowupRow[]>>({});
  const [labels, setLabels] = useState<Record<string, string>>({});
  // case_id → surname of the clinician who recorded the index assessment. Only
  // populated for the PI, whose worklist mixes both sites' patients.
  const [enrollers, setEnrollers] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  // dialog state
  const [extensionOnly, setExtensionOnly] = useState(true);
  const [labelActive, setLabelActive] = useState<CaseRow | null>(null);
  const [labelValue, setLabelValue] = useState('');
  const [savingLabel, setSavingLabel] = useState(false);
  const [labelTipOpen, setLabelTipOpen] = useState(false);
  const [active, setActive] = useState<CaseRow | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [fDate, setFDate] = useState<string>('');
  const [fTsh, setFTsh] = useState<number | null>(null);
  const [fDose, setFDose] = useState<number | null>(null);
  const [fUnchanged, setFUnchanged] = useState(true);
  const [fNotes, setFNotes] = useState('');
  const [saving, setSaving] = useState(false);
  // Serial concordance (exploratory sub-study): the dose proposal for THIS visit.
  // Optional — the registered follow-up TSH must save with or without it.
  const [fProposal, setFProposal] = useState('');
  const [fAlternative, setFAlternative] = useState('');
  const [dialogOpenedAt, setDialogOpenedAt] = useState<number>(0);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data: profile } = await supabase
      .from('profiles').select('id,is_pi').eq('user_id', user.id).single();
    if (!profile) { setLoading(false); return; }
    setProfileId(profile.id);

    // KT (PI) sees every complete case; every other clinician sees only the cases
    // whose index assessment they recorded ("the patients they see").
    let ownCaseIds: string[] | null = null;
    if (!profile.is_pi) {
      const { data: responses } = await supabase
        .from(cfg.clinicianResponses).select('case_id').eq('clinician_id', profile.id);
      ownCaseIds = Array.from(new Set((responses ?? []).map(r => r.case_id)));
      if (ownCaseIds.length === 0) { setCases([]); setFollowups({}); setLabels({}); setLoading(false); return; }
    }

    // cfg.cases is a per-arm union table name → keep the loosened client (union
    // dynamic table names defeat inference, per the useAssessmentSubmit convention).
    let caseQuery = (supabase.from(cfg.cases) as any)
      .select(cfg.caseColumns)
      .eq('status', 'complete')
      .order('created_at', { ascending: true });
    if (ownCaseIds) caseQuery = caseQuery.in('id', ownCaseIds);
    const { data: caseData, error: caseErr } = await caseQuery;

    const ids = (caseData ?? []).map((c: CaseRow) => c.id);
    let fuData: FollowupRow[] = [];
    let fuErr: { message?: string } | null = null;
    if (ids.length > 0) {
      const res = await supabase.from(cfg.followups)
        .select('id,case_id,followup_date,followup_tsh,dose_prescribed,dose_unchanged,on_target,over_treated,notes')
        .in('case_id', ids)
        .order('followup_date', { ascending: true });
      fuData = res.data ?? [];
      fuErr = res.error;
    }

    // Surface backend errors instead of degrading to a silent "no cases" — e.g.
    // if the 20260620000000 migration hasn't been applied to this project yet.
    if (caseErr || fuErr) {
      console.error('[Followups] load failed', caseErr ?? fuErr);
      toast({
        variant: 'destructive',
        title: 'Σφάλμα φόρτωσης επανεξετάσεων',
        description: (caseErr ?? fuErr)?.message ?? 'Άγνωστο σφάλμα',
      });
      setLoading(false);
      return;
    }

    const fuMap: Record<string, FollowupRow[]> = {};
    fuData.forEach((f) => { (fuMap[f.case_id] ??= []).push(f); });

    // Private labels (own case or PI, per RLS). Best-effort; never blocks the list.
    const labelMap: Record<string, string> = {};
    if (ids.length > 0) {
      const { data: labelData } = await supabase
        .from(cfg.labels).select('case_id,label').in('case_id', ids);
      (labelData ?? []).forEach((l) => { labelMap[l.case_id] = l.label; });
    }

    // Who enrolled each case. Only the PI's list mixes clinicians, so nobody else
    // pays for the call. Best-effort: a failure costs the badge, never the list.
    const enrollerMap: Record<string, string> = {};
    if (profile.is_pi && ids.length > 0) {
      const { data: enrollerData, error: enrollerErr } = await supabase
        .rpc('case_enrollers', { p_arm: arm, p_case_ids: ids });
      if (enrollerErr) console.error('[Followups] enrollers fetch failed', enrollerErr);
      (enrollerData ?? []).forEach((e) => { enrollerMap[e.case_id] = e.enroller_label; });
    }

    setCases((caseData ?? []) as CaseRow[]);
    setFollowups(fuMap);
    setLabels(labelMap);
    setEnrollers(enrollerMap);
    setLoading(false);
  }, [user, cfg, arm]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    return cases
      .filter(c => !extensionOnly || c.created_at >= EXTENSION_START)
      .map(c => ({ c, status: deriveStatus(c.created_at, (followups[c.id]?.length ?? 0) > 0) }))
      .sort((a, b) => statusRank[a.status] - statusRank[b.status]
        || new Date(a.c.created_at).getTime() - new Date(b.c.created_at).getTime());
  }, [cases, followups, extensionOnly]);

  const hiddenCount = extensionOnly ? cases.filter(c => c.created_at < EXTENSION_START).length : 0;

  const caseNo = (c: CaseRow) => c.study_case_no != null ? `${cfg.prefix}-${c.study_case_no}` : c.id.slice(0, 8);

  // visit omitted → record a NEW visit; visit given → edit that one in place.
  const openDialog = (c: CaseRow, visit?: FollowupRow) => {
    setActive(c);
    setEditingId(visit?.id ?? null);
    setFDate(visit?.followup_date ?? new Date().toISOString().slice(0, 10));
    setFTsh(visit?.followup_tsh ?? null);
    setFDose(visit?.dose_prescribed ?? null);
    setFUnchanged(visit?.dose_unchanged ?? true);
    setFNotes(visit?.notes ?? '');
    setFProposal('');
    setFAlternative('');
    setDialogOpenedAt(Date.now());
  };

  const closeDialog = () => { setActive(null); setEditingId(null); };

  const save = async () => {
    if (!active || !profileId) return;
    if (fTsh == null) { toast({ variant: 'destructive', title: 'Συμπληρώστε την TSH επανεξέτασης' }); return; }
    if (!fDate) { toast({ variant: 'destructive', title: 'Συμπληρώστε την ημερομηνία επανεξέτασης' }); return; }
    const weeks = weeksBetween(active.created_at, fDate);
    if (weeks < ENTRY_MIN_WEEKS) {
      toast({
        variant: 'destructive',
        title: 'Ελέγξτε την ημερομηνία',
        description: `Η επανεξέταση απέχει ${weeks.toFixed(1)} εβδ. από την εγγραφή — πιθανό λάθος καταχώρησης.`,
      });
      return;
    }
    const { on_target, over_treated } = evalTargets(arm, fTsh, active.gestational_week, weeks);

    setSaving(true);
    const payload = {
      case_id: active.id,
      clinician_id: profileId,
      followup_date: fDate,
      followup_tsh: fTsh,
      dose_prescribed: fDose,
      dose_unchanged: fUnchanged,
      on_target,
      over_treated,
      notes: fNotes || null,
    };
    // Insert a new visit, or update the one being edited. Never upsert on case_id:
    // that overwrote the series and destroyed the registered first-in-window value.
    const { data: savedVisit, error } = editingId
      ? await supabase.from(cfg.followups).update(payload).eq('id', editingId).select().single()
      : await supabase.from(cfg.followups).insert(payload).select().single();

    if (error) {
      setSaving(false);
      const duplicate = (error as { code?: string }).code === '23505';
      toast({
        variant: 'destructive',
        title: duplicate ? 'Υπάρχει ήδη επανεξέταση αυτής της ημερομηνίας' : 'Αποτυχία αποθήκευσης',
        description: duplicate
          ? 'Επεξεργαστείτε την υπάρχουσα εγγραφή ή επιλέξτε άλλη ημερομηνία.'
          : error.message,
      });
      return;
    }

    // The registered outcome is now committed. Everything below is the EXPLORATORY
    // serial-concordance sub-study and runs strictly after, so an algorithm failure
    // can never cost us the follow-up TSH.
    let serialNote = '';
    if (fProposal && savedVisit?.id) {
      const outcome = await scoreSerialConcordance(
        arm,
        savedVisit.id,
        active.id,
        profileId,
        { recommended_dose: fProposal, alternative_dose: fAlternative },
        fTsh,
        weeks,
        fDose,
        Math.floor((Date.now() - dialogOpenedAt) / 1000),
      );
      if (outcome.status === 'api_failed') {
        serialNote = ' · Η σύγκριση αλγορίθμου δεν ολοκληρώθηκε (η επανεξέταση αποθηκεύτηκε).';
        console.warn('[serial]', outcome.userMessage);
      }
      // The tier is deliberately NOT surfaced: revealing it here would anchor the
      // clinician's next follow-up decision, compounding the bias the sub-study is
      // already bounded by.
    }
    setSaving(false);

    toast({
      title: `Επανεξέταση ${editingId ? 'ενημερώθηκε' : 'καταχωρήθηκε'} (${caseNo(active)})`,
      description:
        (on_target ? 'Εντός στόχου ✓' : (over_treated ? 'Υπερδιόρθωση (TSH<0.1)' : 'Εκτός στόχου')) +
        serialNote,
    });
    closeDialog();
    load();
  };

  const openLabel = (c: CaseRow) => { setLabelActive(c); setLabelValue(labels[c.id] ?? ''); };

  const saveLabelRow = async () => {
    if (!labelActive || !profileId) return;
    setSavingLabel(true);
    const { error } = await supabase.from(cfg.labels).upsert({
      case_id: labelActive.id, clinician_id: profileId, label: labelValue.trim(),
      updated_at: new Date().toISOString(),
    }, { onConflict: 'case_id' });
    setSavingLabel(false);
    if (error) { toast({ variant: 'destructive', title: 'Αποτυχία αποθήκευσης', description: error.message }); return; }
    setLabels(prev => ({ ...prev, [labelActive.id]: labelValue.trim() }));
    toast({ title: 'Το ιδιωτικό σημείωμα αποθηκεύτηκε' });
    setLabelActive(null);
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  const dueCount = rows.filter(r => r.status === 'due' || r.status === 'overdue').length;

  // Live interval feedback for the open dialog.
  const fWeeks = active && fDate ? weeksBetween(active.created_at, fDate) : null;
  const fMinDate = active ? addWeeksISO(active.created_at, ENTRY_MIN_WEEKS) : undefined;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 border-b pb-2">
        <div className="flex items-center gap-2">
          <Switch id={`ext-${arm}`} checked={extensionOnly} onCheckedChange={setExtensionOnly} />
          <Label htmlFor={`ext-${arm}`} className="text-sm">Μόνο επέκταση (από 1 Ιουλ 2026)</Label>
        </div>
        {hiddenCount > 0 && (
          <span className="text-xs text-gray-500">{hiddenCount} Historical Cases πριν από έναρξη spin-off, correctness study</span>
        )}
      </div>
      <p className="text-sm text-gray-600">
        {dueCount > 0
          ? `${dueCount} επανεξετάσεις σε εκκρεμότητα (παράθυρο T0 + 4–8 εβδομάδες).`
          : 'Καμία επανεξέταση σε εκκρεμότητα.'}
      </p>

      {rows.length === 0 && (
        <p className="text-sm text-gray-500">Δεν υπάρχουν ολοκληρωμένες υποθέσεις για παρακολούθηση.</p>
      )}

      {rows.map(({ c, status }) => {
        const visits = followups[c.id] ?? [];
        return (
          <div key={c.id} className="flex items-start justify-between gap-3 border rounded-md p-3 text-sm bg-white">
            <div className="space-y-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono font-semibold">{caseNo(c)}</span>
                {enrollers[c.id] && (
                  <Badge
                    variant="outline"
                    title="Ιατρός που κατέγραψε την αρχική αξιολόγηση"
                    style={{ borderColor: 'var(--brand-edge)', backgroundColor: 'var(--brand-wash)', color: 'var(--brand)' }}
                  >
                    <UserRound className="w-3 h-3 mr-1" />{enrollers[c.id]}
                  </Badge>
                )}
                {status === 'done' && (
                  <Badge variant="outline" className="border-green-400 text-green-700">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    {visits.length} {visits.length === 1 ? 'επανεξέταση' : 'επανεξετάσεις'}
                  </Badge>
                )}
                {status === 'overdue' && (
                  <Badge variant="outline" className="border-red-400 text-red-700">
                    <AlertTriangle className="w-3 h-3 mr-1" />Εκπρόθεσμη
                  </Badge>
                )}
                {status === 'due' && (
                  <Badge variant="outline" className="border-orange-400 text-orange-700">
                    <Clock className="w-3 h-3 mr-1" />Σε εκκρεμότητα
                  </Badge>
                )}
                {status === 'scheduled' && (
                  <Badge variant="outline" className="border-gray-300 text-gray-500">
                    <CalendarClock className="w-3 h-3 mr-1" />Προγραμματισμένη
                  </Badge>
                )}
              </div>
              <div className="text-gray-700">
                Εγγραφή: {new Date(c.created_at).toLocaleDateString('el-GR')} · TSH αρχική {c.tsh}
                {c.gestational_week !== undefined && ` · Εβδ. ${c.gestational_week}`}
              </div>
              {visits.length > 0 && (
                <ul className="space-y-0.5 pt-1">
                  {visits.map((v, i) => {
                    const wk = weeksBetween(c.created_at, v.followup_date);
                    return (
                      <li key={v.id} className="flex items-center gap-2 flex-wrap text-xs">
                        <span className="text-gray-400 font-mono">#{i + 1}</span>
                        <span>{new Date(v.followup_date).toLocaleDateString('el-GR')}</span>
                        <span className="text-gray-500">({wk.toFixed(1)} εβδ.)</span>
                        <span className="font-medium">TSH {v.followup_tsh}</span>
                        <span className={v.on_target ? 'text-green-700' : 'text-red-700'}>
                          {v.on_target ? 'εντός' : (v.over_treated ? 'υπερδιόρθωση' : 'εκτός')}
                        </span>
                        {wk >= PP_MIN_WEEKS && wk <= PP_MAX_WEEKS && v.dose_unchanged && (
                          <Badge variant="outline" className="h-4 px-1 text-[10px] border-blue-300 text-blue-700">
                            4–8 εβδ. · αμετάβλητη
                          </Badge>
                        )}
                        <button
                          type="button"
                          className="text-gray-500 underline hover:text-gray-800"
                          onClick={() => openDialog(c, v)}
                        >
                          επεξεργασία
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
              {labels[c.id] && (
                <div className="text-xs text-gray-500 italic truncate">Σημείωμα: {labels[c.id]}</div>
              )}
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <Button size="sm" variant={visits.length > 0 ? 'outline' : 'default'} onClick={() => openDialog(c)}>
                {visits.length > 0 ? '+ Επανεξέταση' : 'Καταχώρηση'}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-600" onClick={() => openLabel(c)}>
                <Tag className="w-3 h-3 mr-1" />{labels[c.id] ? 'Σημείωμα' : '+ Σημείωμα'}
              </Button>
            </div>
          </div>
        );
      })}

      <Dialog open={!!active} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Επεξεργασία επανεξέτασης' : 'Νέα επανεξέταση'} {active ? caseNo(active) : ''}
            </DialogTitle>
            <DialogDescription>
              Καταχώρηση της TSH της συνήθους φροντίδας. Το πρωτόκολλο ορίζει ως κύρια έκβαση την
              <b> πρώτη</b> TSH σε 4–8 εβδομάδες με αμετάβλητη δόση· επιπλέον επανεξετάσεις
              καταγράφονται χωριστά και δεν αντικαθιστούν την πρώτη.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="fdate">Ημερομηνία επανεξέτασης</Label>
              <Input
                id="fdate"
                type="date"
                min={fMinDate}
                value={fDate}
                onChange={(e) => setFDate(e.target.value)}
              />
              {/* Neutral interval only. Deliberately does NOT say which analysis set the
                  date lands in — surfacing that here would invite nudging the date into
                  the per-protocol window. v_followup_visits classifies it. */}
              {fWeeks !== null && (
                <p className={`text-xs ${fWeeks < ENTRY_MIN_WEEKS ? 'text-red-600' : 'text-gray-500'}`}>
                  {fWeeks.toFixed(1)} εβδ. από την εγγραφή
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="ftsh">TSH επανεξέτασης (mIU/L)</Label>
              <DecimalInput id="ftsh" value={fTsh} onValueChange={setFTsh} placeholder="π.χ. 2,1" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="fdose">Δόση που λαμβάνει (mcg/ημ.) — προαιρετικό</Label>
              <DecimalInput id="fdose" value={fDose} onValueChange={setFDose} placeholder="π.χ. 75" />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="funch">Αμετάβλητη δόση από την εγγραφή</Label>
              <Switch id="funch" checked={fUnchanged} onCheckedChange={setFUnchanged} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="fnotes">Σημειώσεις — προαιρετικό</Label>
              <Textarea id="fnotes" value={fNotes} onChange={(e) => setFNotes(e.target.value)} rows={2} />
            </div>

            {/* Serial concordance (exploratory sub-study). The algorithm runs silently
                on save and its output is never rendered here — showing it would anchor
                the next visit's decision. */}
            {!editingId && (
              <div className="space-y-3 rounded-md border-t pt-4">
                <div>
                  <h4 className="text-sm font-semibold" style={{ color: 'var(--brand)' }}>
                    Η δοσολογική σας πρόταση για αυτή την επανεξέταση
                  </h4>
                  <p className="text-xs text-gray-500">
                    Προαιρετικό. Συμπληρώστε το πριν από οποιαδήποτε άλλη ενέργεια, με βάση την
                    παρούσα TSH. Δεν εμφανίζεται σύσταση του αλγορίθμου.
                  </p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="fprop">Προτεινόμενη δοσολογία</Label>
                  <DoseCombobox
                    value={fProposal}
                    placeholder="Πληκτρολογήστε ή επιλέξτε δοσολογία..."
                    onChange={setFProposal}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="falt">Εναλλακτική δοσολογία — προαιρετικό</Label>
                  <DoseCombobox
                    value={fAlternative}
                    placeholder="Πληκτρολογήστε ή επιλέξτε δοσολογία..."
                    onChange={setFAlternative}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={saving}>Άκυρο</Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Αποθήκευση
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!labelActive} onOpenChange={(o) => !o && setLabelActive(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ιδιωτικό σημείωμα {labelActive ? caseNo(labelActive) : ''}</DialogTitle>
            <DialogDescription>
              Ορατό μόνο σε εσάς & τον Υπεύθυνο Ερευνητή. Δεν αποτελεί ερευνητικό δεδομένο.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              value={labelValue}
              onChange={(e) => setLabelValue(e.target.value)}
              placeholder="Προσθέστε αρχικά ή μνημονικό κανόνα"
            />
            <Collapsible open={labelTipOpen} onOpenChange={setLabelTipOpen}>
              <CollapsibleTrigger className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                <Info className="w-3 h-3" /> Τι είναι αυτό;
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-1 rounded border bg-gray-50 p-2 text-xs text-gray-600">
                Προσθέστε <b>αρχικά ή μνημονικό κανόνα</b> για να αναγνωρίζετε την ασθενή στην επανεξέταση.
                Ορατό μόνο σε εσάς και τον Υπεύθυνο Ερευνητή· δεν εξάγεται στην ανάλυση.
                Μην χρησιμοποιείτε πλήρες ονοματεπώνυμο.
              </CollapsibleContent>
            </Collapsible>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLabelActive(null)} disabled={savingLabel}>Άκυρο</Button>
            <Button onClick={saveLabelRow} disabled={savingLabel}>
              {savingLabel && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Αποθήκευση
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Followups = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => { if (user === null) navigate('/auth'); }, [user, navigate]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8f7f2' }}>
      <header className="border-b bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-bold" style={{ color: 'var(--brand)' }}>Επανεξετάσεις (Follow-up TSH)</h1>
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />Επιστροφή
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Καταχώρηση έκβασης TSH</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="pregnancy">
              <TabsList className="mb-4">
                <TabsTrigger value="pregnancy">Κύηση</TabsTrigger>
                <TabsTrigger value="preconception">Προ της σύλληψης</TabsTrigger>
              </TabsList>
              <TabsContent value="pregnancy"><ArmFollowups arm="pregnancy" /></TabsContent>
              <TabsContent value="preconception"><ArmFollowups arm="preconception" /></TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Followups;
